import { type SendMessageRequest, type SendMessageResponse } from "@common/components/AiChat/types";
import apiV1, { getApiPrefix } from "@common/lib/api";
import { type I18n } from "@common/lib/i18n";
import { type Ai } from "./types";

const createApi = (i18n: I18n, isHost: boolean) => {
  const baseUrl = isHost ? `${getApiPrefix(isHost)}/ai` : "/ai";

  return {
    agents: {
      add: (data: Ai.Agents.Api.CreateAgent): Promise<Ai.Agents.Api.Agent> =>
        apiV1.post(i18n, `${baseUrl}/agents`, data),
      getAll: (): Promise<Ai.Agents.Api.Agent[]> => apiV1.get(i18n, `${baseUrl}/agents`),
      get: (id: string): Promise<Ai.Agents.Api.Agent> => apiV1.get(i18n, `${baseUrl}/agents/${id}`),
      getAllAggregated: (): Promise<Ai.Agents.Api.AgentWithAggregates[]> =>
        apiV1.get(i18n, `${baseUrl}/agents`, { includeAggregates: true }),
      getAggregated: (id: string): Promise<Ai.Agents.Api.AgentWithAggregates> =>
        apiV1.get(i18n, `${baseUrl}/agents/${id}`, { includeAggregates: true }),
      update: (data: Ai.Agents.Api.UpdateAgent): Promise<Ai.Agents.Api.Agent> =>
        apiV1.put(i18n, `${baseUrl}/agents`, data),
      delete: (id: string): Promise<void> => apiV1.delete(i18n, `${baseUrl}/agents/${id}`),

      getArticleCategories: (id: string): Promise<Ai.Agents.Api.ArticleCategory[]> =>
        apiV1.get(i18n, `${baseUrl}/agents/${id}/article/categories`),
      getArticles: (id: string): Promise<Ai.Agents.Api.Article[]> =>
        apiV1.get(i18n, `${baseUrl}/agents/${id}/articles`),
      getDocumentScopes: (id: string): Promise<Ai.Agents.Api.DocumentScope[]> =>
        apiV1.get(i18n, `${baseUrl}/agents/${id}/document/scopes`)
    },
    chats: {
      add: (agentId: string): Promise<Ai.Chat.Api.Chat> => apiV1.post(i18n, `${baseUrl}/chats`, { agentId }),
      get: (id: string): Promise<Ai.Chat.Api.Chat> => apiV1.get(i18n, `${baseUrl}/chats/${id}`),
      getAll: (agentId?: string): Promise<Ai.Chat.Api.Chat[]> => apiV1.get(i18n, `${baseUrl}/chats`, { agentId }),
      update: (data: Ai.Chat.Api.UpdateChat): Promise<Ai.Chat.Api.Chat> => apiV1.put(i18n, `${baseUrl}/chats`, data),
      sendMessage: (id: string, data: SendMessageRequest): Promise<SendMessageResponse> =>
        apiV1.post(i18n, `${baseUrl}/chats/${id}/sendMessage`, data)
    },
    chatMessages: {
      get: (id: string, mid: string): Promise<Ai.ChatMessage.Api.ChatMessage> =>
        apiV1.get(i18n, `${baseUrl}/chats/${id}/messages/${mid}`),
      getAll: (id: string): Promise<Ai.ChatMessage.Api.ChatMessage[]> =>
        apiV1.get(i18n, `${baseUrl}/chats/${id}/messages`),
      update: (
        id: string,
        mid: string,
        data: Ai.ChatMessage.Api.UpdateChatMessage
      ): Promise<Ai.ChatMessage.Api.ChatMessage> => apiV1.put(i18n, `${baseUrl}/chats/${id}/messages/${mid}`, data)
    },
    credits: {
      getPackages: (): Promise<Ai.Credits.Api.CreditPackage[]> => apiV1.get(i18n, `${baseUrl}/credits/packages`)
    },
    text: {
      generate: (data: Ai.Text.Api.GenerateRequest): Promise<string> =>
        apiV1.post(i18n, `${baseUrl}/text/generate`, data),
      edit: (data: Ai.Text.Api.EditRequest): Promise<string> => apiV1.post(i18n, `${baseUrl}/text/edit`, data)
    }
  };
};

export const getApi = (i18n: I18n, isHost?: boolean) => createApi(i18n, isHost ?? false);
